import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ExternalLink, Building2, Clock, CheckCircle2 } from 'lucide-react';
import FAQSection from "../components/FAQSection";
import { caseStudiesFaqs } from '../data/faqs';
import BlogSection from "../components/BlogSection"; // adjust path if needed

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CaseStudies = () => {

  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudy, setSelectedStudy] = useState(null);

  useEffect(() => {
    fetchCaseStudies();
  }, []);

  const fetchCaseStudies = async () => {

    try {

      const response = await axios.get(`${API}/case-studies`);
      const data = response.data;

      if (Array.isArray(data)) {
        setCaseStudies(data);
      } 
      else if (Array.isArray(data?.data)) {
        setCaseStudies(data.data);
      } 
      else {
        setCaseStudies([]);
      }

    } catch (error) {

      console.error('Error fetching case studies:', error);
      setCaseStudies([]);

    }

    setLoading(false);
  };

  return (

    <>

      <Helmet>

        <title>Case Studies | Novaseeds Technologies</title>

        <meta
          name="description"
          content="Explore real-world case studies from Novaseeds Technologies showcasing innovative software solutions, challenges solved, and measurable business results."
        />

        {/* FAQ Schema */}

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": caseStudiesFaqs.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": faq.answer
                }
              }))
            })
          }}
        />

      </Helmet>

      <div className="min-h-screen pt-32 pb-20 bg-background">

        <div className="container mx-auto px-4 md:px-6 max-w-7xl">

          {/* HERO */}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-20"
          >

            <span className="text-sm uppercase tracking-widest text-muted-foreground font-bold">
              CASE STUDIES
            </span>

            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-none mt-4 mb-6">
              <span className="text-foreground">Success Stories</span><br />
              <span className="text-gradient">That Inspire Innovation</span>
            </h1>

            <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-3xl mx-auto">
              Real projects, real results. See how we've helped businesses transform through technology.
            </p>

          </motion.div>

          {/* CASE STUDY GRID */}

          {loading ? (

            <div className="text-center py-20">

              <div className="inline-block w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>

              <p className="text-muted-foreground mt-4">
                Loading case studies...
              </p>

            </div>

          ) : (

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">

              {(caseStudies || []).map((study, index) => (

                <motion.div
                  key={study?.id || index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => setSelectedStudy(study)}
                  className="bg-card border border-[#E2E8F0] shadow-[0_2px_8px_rgba(0,0,0,0.05)] rounded-3xl overflow-hidden hover:shadow-lg transition-all hover:-translate-y-1 cursor-pointer group"
                >

                  <div className="relative h-56 overflow-hidden">

                    <img
                      src={study?.image}
                      alt={study?.title || "Case Study"}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>

                  </div>

                  <div className="p-6">

                    <div className="flex items-center space-x-2 mb-3">

                      <span className="bg-blue-50 border border-blue-200 rounded-full px-3 py-1 text-xs text-blue-700 font-medium">
                        {study?.industry}
                      </span>

                      <div className="flex items-center space-x-1 text-muted-foreground text-xs">
                        <Clock size={12} />
                        <span>{study?.duration}</span>
                      </div>

                    </div>

                    <h3 className="text-xl font-semibold text-foreground mb-2">
                      {study?.title}
                    </h3>

                    <div className="flex items-center space-x-2 text-muted-foreground text-sm mb-3">
                      <Building2 size={14} />
                      <span>{study?.client}</span>
                    </div>

                    <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                      {study?.description}
                    </p>

                    <button className="text-blue-600 hover:text-blue-700 text-sm font-medium inline-flex items-center space-x-1 group-hover:underline">
                      <span>View Case Study</span>
                      <ExternalLink size={14} />
                    </button>

                  </div>

                </motion.div>

              ))}

            </div>

          )}

          {/* MODAL */}

          {selectedStudy && (

            <div
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedStudy(null)}
            >

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-card border border-[#E2E8F0] shadow-[0_2px_8px_rgba(0,0,0,0.05)] rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              >

                <div className="relative h-72">

                  <img
                    src={selectedStudy?.image}
                    alt={selectedStudy?.title}
                    className="w-full h-full object-cover"
                  />

                  <button
                    onClick={() => setSelectedStudy(null)}
                    className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/95 border border-[#E2E8F0] text-foreground shadow-sm"
                  >
                    ✕
                  </button>

                </div>

                <div className="p-8">

                  <h2 className="text-4xl font-bold text-foreground mb-6">
                    {selectedStudy?.title}
                  </h2>

                  <div className="mb-8">

                    <h3 className="text-xl font-semibold text-foreground mb-4">
                      Results
                    </h3>

                    <div className="space-y-3">

                      {(selectedStudy?.results || []).map((result, idx) => (

                        <div key={idx} className="flex items-start space-x-3">

                          <CheckCircle2 size={20} className="text-green-600 flex-shrink-0 mt-0.5" />

                          <span className="text-muted-foreground">{result}</span>

                        </div>

                      ))}

                    </div>

                  </div>

                </div>

              </motion.div>

            </div>

          )}

          {/* FAQ SECTION */}

         <FAQSection faqs={ caseStudiesFaqs} title=" Frequently Asked Questions" />
         {/* Blog Section */}
         <BlogSection categorySlug="mobile-app-development" title="Market Trends" maxPosts={3} />
        </div>

      </div>

    </>

  );
};